import React, { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { GroupedFieldSettings } from "@truevault/aquilo";
import { Api } from "../../../../../common/service/Api";
import {
  FlowDownFieldVendorDto,
  PrivacyCenterDto,
} from "../../../../../common/service/server/dto/PrivacyCenterDto";
import { OrganizationPublicId } from "../../../../../common/service/server/Types";
import { usePrivacyCenter } from "../../../../hooks/usePrivacyCenter";
import {
  createFlowDownAutosave,
  flowDownFieldId,
  FlowDownFieldStatus,
  groupFlowDownFields,
  updateFlowDownPermission,
} from "./flowDownFields";

type FlowDownSettingsProps = {
  organizationId: OrganizationPublicId;
  source: FlowDownFieldVendorDto["source"];
};

function FlowDownFields({ organizationId, source }: FlowDownSettingsProps) {
  const [privacyCenter] = usePrivacyCenter(organizationId, !organizationId);
  const client = useQueryClient();
  const [statuses, setStatuses] = useState<Record<string, FlowDownFieldStatus>>({});
  const fields = (privacyCenter?.flowDownFields ?? []).filter((field) => field.source === source);
  const save = useMemo(() => {
    const queryKey = ["privacyCenter", organizationId];
    const previewQueryKey = ["vendorMessageTemplatePreview", organizationId];
    return createFlowDownAutosave({
      persist: (field, enabled) =>
        Api.privacyCenter.updateFlowDownField(organizationId, field.source, field.key, enabled),
      cancelReads: async () => {
        await Promise.all([
          client.cancelQueries({ queryKey, exact: true }),
          client.cancelQueries({ queryKey: previewQueryKey }),
        ]);
      },
      commit: (field, enabled) =>
        client.setQueryData<PrivacyCenterDto[]>(queryKey, (current) =>
          updateFlowDownPermission(current, field, enabled),
        ),
      refresh: () =>
        Promise.all([
          client.invalidateQueries({ queryKey, exact: true }),
          client.invalidateQueries({ queryKey: previewQueryKey }),
        ]),
      onStatus: (id, status) =>
        setStatuses((current) => {
          const next = { ...current };
          if (status) next[id] = status;
          else delete next[id];
          return next;
        }),
    });
  }, [client, organizationId]);

  return (
    <GroupedFieldSettings
      title="Additional consumer information"
      description={`Choose which ${
        source === "REQUEST_FORM" ? "Request Form" : "Opt-Out Form"
      } details to share with vendors${
        source === "LIMIT"
          ? " for Limit requests"
          : source === "OPT_OUT_FORM"
          ? " for Opt-Out requests"
          : ""
      }. Checked fields appear in vendor emails when provided by the consumer.${
        source !== "REQUEST_FORM" ? " Limit and Opt-Out selections are independent." : ""
      }`}
      groups={groupFlowDownFields(fields).filter((group) => group.id === source)}
      selection={Object.fromEntries(
        fields.map((field) => [
          flowDownFieldId(field),
          statuses[flowDownFieldId(field)]?.optimisticValue ?? field.includeInVendorRequests,
        ]),
      )}
      statuses={statuses}
      disabled={!privacyCenter}
      onChange={(id, enabled) => {
        const field = fields.find((item) => flowDownFieldId(item) === id);
        if (field) void save(field, enabled);
      }}
      emptyMessage={
        !privacyCenter ? "Loading fields…" : "No additional fields enabled for this form."
      }
    />
  );
}

export function VendorRequestCustomFieldSettings(props: FlowDownSettingsProps) {
  // Pending state belongs to one organization/permission scope, even during navigation.
  return <FlowDownFields key={`${props.organizationId}:${props.source}`} {...props} />;
}
